import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { useSplit } from './queries';
import { schema, type FormFields } from './schema';
import { ControlledTextField } from '../../../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../../../components/FormControlsV2/FormProvider';
import { SearchInstrumentField } from '../../../../../../components/SearchInstrumentField';
import { parseApiError, isErrorResponse } from '../../../../../../utils/apiErrors';

interface Props {
  onUpdate: () => void;
}

export function Form({ onUpdate }: Props) {
  const [position, setPosition] = useState<string>('');
  const [newPosition, setNewPosition] = useState<string>('');
  const [error, setError] = useState<string>('');

  const { mutateAsync, isPending: isLoading } = useSplit();

  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormFields>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      coefficient: 0,
    },
  });

  const onSearchPositionClick = (securityKey: string) => {
    setPosition(securityKey);
  };
  const handleSearchPositionClear = () => {
    setPosition('');
  };

  const onSearchNewPositionClick = (securityKey: string) => {
    setNewPosition(securityKey);
  };
  const handleSSearchNewPositionClear = () => {
    setNewPosition('');
  };

  const onSubmit = async (values: FormFields) => {
    const [symbol, classCode, board] = newPosition.split(' ');

    const data = {
      strategyId: '00000000-0000-0000-0000-000000000000',
      symbol,
      classCode,
      board,
      weight: Number(values.coefficient),
      securityKey2: position,
    };

    try {
      const result = await mutateAsync(data);

      if (isErrorResponse(result)) {
        setError(parseApiError(result));
        return;
      }

      onUpdate();
      enqueueSnackbar('Позиция разделена');
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  return (
    <FormProvider control={control} errors={errors}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <SearchInstrumentField onSearch={onSearchPositionClick} onClear={handleSearchPositionClear} />
          <SearchInstrumentField onSearch={onSearchNewPositionClick} onClear={handleSSearchNewPositionClear} />
          <ControlledTextField type="number" name="coefficient" label="Коэффициент" fullWidth />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading || !isValid || !position || !newPosition}
            fullWidth
            sx={{ mt: 2 }}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            Заменить
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
}
